"use client";
import Container from "../container";
import BlogCard from "./BlogCard";
import styles from "./blog.module.css";
import { getBlogs } from "./getBlogs";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { locationcontext } from "@/lib/context";
import {
  buildDynamicContext,
  resolveDynamicVariables,
} from "@/lib/replaceDynamicValues";

import { Col, Pagination, Row } from "antd";

// `initialBlogs` / `initialTotal` / `initialPage` come from ServerBlogList so
// the posts (and links to them) are in the server HTML for crawlers.
export default function BlogList({
  config = {},
  data,
  context,
  initialBlogs,
  initialTotal = 0,
  initialPage = 1,
  initialCity,
}) {
  const hasInitial = Array.isArray(initialBlogs);
  const pathname = usePathname();
  const pageSize = config.limit ?? 12;

  // Blog posts are fetched at runtime from the blogs API, not read from the
  // CMS page's own section config — so they never pass through the
  // `resolveDynamicVariables` call CmsRenderer does for section data, and
  // any `{{current_city}}`-style token authored in a post's title/heading/
  // subheading was rendering as literal text. Resolve it here instead,
  // using the visitor's own location context (falls back to a static
  // context if a `context` prop is ever wired in from the server).
  const { location, locationState, locationCountry, locationPincode } =
    useContext(locationcontext);
  const dynamicContext = useMemo(
    () =>
      context ??
      buildDynamicContext({
        city: location || "ahmedabad",
        state: locationState,
        country: locationCountry,
        pincode: locationPincode,
      }),
    [context, location, locationState, locationCountry, locationPincode],
  );

  const [blogs, setBlogs] = useState(() =>
    hasInitial ? resolveDynamicVariables(initialBlogs, dynamicContext) : [],
  );
  const [loading, setLoading] = useState(!hasInitial);
  const [total, setTotal] = useState(hasInitial ? initialTotal : 0);
  const [page, setPage] = useState(initialPage);

  // The server already rendered this page for this city; only refetch once
  // something actually changes (page, city, config).
  const skipFetchRef = useRef(
    hasInitial &&
      (!initialCity ||
        (location || "").toLowerCase() === initialCity.toLowerCase()),
  );

  const fetchData = async (pageNum) => {
    setLoading(true);
    const { data: fetchedData, total: fetchedTotal } = await getBlogs({
      limit: pageSize,
      page: pageNum,
      endpoint: config.endpoint,
      city: location,
    });

    setBlogs(resolveDynamicVariables(fetchedData, dynamicContext));
    setTotal(fetchedTotal);
    setLoading(false);
  };

  useEffect(() => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    fetchData(page);
  }, [page, config.limit, config.endpoint, dynamicContext]);

  const pageHref = (p) => (p > 1 ? `${pathname}?page=${p}` : pathname);

  const handlePageChange = (p) => {
    setPage(p);
    window.history.replaceState(window.history.state, "", pageHref(p));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Real <a href="?page=N"> links so crawlers can walk every listing page;
  // the click itself stays client-side (antd's onChange handles it).
  const renderPageItem = (p, type, original) =>
    type === "page" ? (
      <a href={pageHref(p)} onClick={(e) => e.preventDefault()}>
        {p}
      </a>
    ) : (
      original
    );

  if (loading && page === 1) {
    return (
      <div className="py-20 text-center">
        <Container>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      {/* ── Grid & Pagination ── */}
      <Container className={styles.listingGrid}>
        {blogs.length === 0 ? (
          <div
            className="py-16 text-center"
            style={{ color: "var(--color-grey)" }}
          >
            No blogs found.
          </div>
        ) : (
          <>
            <Row gutter={[24, 24]} className={styles.blogRow}>
              {blogs.map((blog, i) => (
                <Col
                  key={blog.id || blog.slug || i}
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                  className={styles.blogCol}
                >
                  <BlogCard item={blog} />
                </Col>
              ))}
            </Row>

            <div className={styles.listingPagination}>
              <Pagination
                current={page}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                itemRender={renderPageItem}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </Container>
    </>
  );
}
